/**
 * Hook that fetches every building in the effective org plus every room in
 * every building, returning two flat arrays. Used by the "Download all"
 * row on the Exports page.
 *
 * Keeps concurrency capped at MAX_CONCURRENCY so a 1000-room org doesn't
 * fire 1000 simultaneous requests.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/services'
import type { Building, Room } from '@/types'

/** Upper bound on in-flight /rooms fetches during a "Download all" fan-out. */
export const MAX_CONCURRENCY = 8

export interface OrgRoomsData {
  buildings: Building[]
  rooms: Room[]
}

/**
 * Runs `task` over `items` with at most `concurrency` promises in flight
 * at any moment. Order of results matches input order.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  task: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let next = 0
  async function worker() {
    while (true) {
      const idx = next++
      if (idx >= items.length) return
      results[idx] = await task(items[idx])
    }
  }
  const workers: Promise<void>[] = []
  const n = Math.min(Math.max(1, concurrency), items.length)
  for (let i = 0; i < n; i++) {
    workers.push(worker())
  }
  await Promise.all(workers)
  return results
}

/**
 * React Query hook that loads all buildings for the effective org, then
 * all rooms across those buildings. `enabled` is gated on the org id so
 * super_admin without a selection doesn't trigger a request.
 */
export function useOrgRooms(effectiveOrgId: string) {
  return useQuery<OrgRoomsData>({
    queryKey: ['exports', 'org-rooms', effectiveOrgId],
    enabled: !!effectiveOrgId,
    queryFn: async () => {
      const buildings = await api.get<Building[]>('/buildings')
      const roomsPerBuilding = await mapWithConcurrency(
        buildings,
        MAX_CONCURRENCY,
        (b) => api.get<Room[]>(`/buildings/${b.id}/rooms`)
      )
      const rooms = roomsPerBuilding.flat()
      return { buildings, rooms }
    },
  })
}
